import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const ACTIVE_GROUP_KEY = 'hive.activeGroupId';

export type GroupSummary = {
  id: string;
  name: string;
  join_code: string;
  owner_id: string;
  expires_at: string | null;
};

export function useMyGroups(selfId: string) {
  const [groups, setGroups] = useState<GroupSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select('groups!inner(id, name, join_code, owner_id, expires_at)')
        .eq('user_id', selfId);
      if (cancelled) return;
      if (error) {
        setError(error.message);
        setGroups([]);
        return;
      }
      const list = (data ?? []).map((r: any) => r.groups as GroupSummary).filter(Boolean);
      setGroups(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [selfId, version]);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  return { groups, error, refresh };
}

export function useActiveGroup(selfId: string) {
  const { groups, error, refresh } = useMyGroups(selfId);
  const [activeId, setActiveId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ACTIVE_GROUP_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!groups) return;
    const stillValid = activeId && groups.some((g) => g.id === activeId);
    if (!stillValid) {
      const next = groups[0]?.id ?? null;
      setActiveId(next);
      if (next) {
        try {
          localStorage.setItem(ACTIVE_GROUP_KEY, next);
        } catch {}
      }
    }
  }, [groups, activeId]);

  const switchTo = useCallback((id: string) => {
    setActiveId(id);
    try {
      localStorage.setItem(ACTIVE_GROUP_KEY, id);
    } catch {}
  }, []);

  const active = groups?.find((g) => g.id === activeId) ?? null;

  return { groups, active, switchTo, refresh, error, loading: groups === null };
}
